import React, { useEffect, useState } from 'react';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { addDedication } from "../../slices/event/dedication/thunk";
import { dedicationReset } from "../../slices/event/dedication/reducer";
import { viewEventSongs } from "../../slices/event/view/thunk";
import { notificationService } from "../../helpers/NotificationService";

const MAX_TEXT_LENGTH = 150;
const MIN_TEXT_LENGTH = 2;

/**
 * A dedication sent on its own, without putting a song on the list. When the event
 * requires a tip, the payment gate runs first and the dedication is sent afterwards
 * (onPay), exactly like adding a song.
 */
export const AddDedicationModal = ({ isOpen, toggle, eventId, guestId, requireTip, onPay }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [text, setText] = useState('');
  const [error, setError] = useState(null);

  const selectDedication = createSelector(
    (state) => state.Dedication,
    (slice) => ({
      dedicationSuccess: slice.success,
      dedicationError: slice.errorMessage,
      dedicationSaving: slice.saving,
    })
  );
  const { dedicationSuccess, dedicationError, dedicationSaving } = useSelector(selectDedication);

  useEffect(() => {
    if (isOpen) {
      setText('');
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (dedicationSuccess) {
      dispatch(dedicationReset());
      dispatch(viewEventSongs(eventId));
      if (isOpen) toggle();
    }
  }, [dedicationSuccess]);

  useEffect(() => {
    if (dedicationError) {
      if (isOpen) {
        setError(t(dedicationError));
      } else {
        notificationService.error(t(dedicationError));
      }
      dispatch(dedicationReset());
    }
  }, [dedicationError]);

  const handleSubmit = () => {
    const trimmed = text.trim();

    if (trimmed.length < MIN_TEXT_LENGTH) {
      setError(t('MIN_2_CHARS'));
      return;
    }

    const payload = { id: guestId, eventId, text: trimmed };

    if (requireTip) {
      onPay(payload);
      return;
    }

    dispatch(addDedication(payload));
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{t('Add a dedication')}</ModalHeader>
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <ModalBody>
          <Input
            type="textarea"
            rows="3"
            maxLength={MAX_TEXT_LENGTH}
            autoFocus
            className={error ? 'is-invalid' : ''}
            placeholder={t('Enter dedication')}
            value={text}
            onChange={(e) => { setText(e.target.value); setError(null); }}
          />
          {error && <div className="invalid-feedback d-block">{error}</div>}
          <div className="form-text d-flex justify-content-between">
            <span>{t('The host will read it out during the event.')}</span>
            <span>{text.length}/{MAX_TEXT_LENGTH}</span>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-end">
            <button type="button" className="btn btn-label btn-light" onClick={toggle}>
              <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>{t('Close')}
            </button>
            <button type="submit" className="btn btn-label btn-success color-primary" disabled={dedicationSaving}>
              <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
              {requireTip ? t('Pay and send') : t('Save')}
            </button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};
